import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { initializeApp, deleteApp } from "firebase/app";
import {
  getAuth,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updatePassword,
} from "firebase/auth";
import { doc, setDoc, updateDoc } from "firebase/firestore";
import { db, firebaseConfig } from "../../../firebase";
import styles from "../../../styles/AdminConsole.module.css";
import { COUNTRIES, TEAM_CATEGORIES } from "../../../data/constants";
import StatCard from "../shared/components/StatCard";
import SelectWrapper from "../shared/components/SelectWrapper";
import Toggle from "../shared/components/Toggle";
import Pagination from "../shared/components/Pagination";
import CategoryBadge from "../shared/components/CategoryBadge";
import Modal from "../shared/components/Modal";

const ITEMS_PER_PAGE = 15;

export default function TeamTab({ users, setUsers, showToast }) {
  const navigate = useNavigate();

  // ── Create form ───────────────────────────────────────────────────────────
  const [tmUsername, setTmUsername] = useState("");
  const [tmPassword, setTmPassword] = useState("");
  const [tmCategory, setTmCategory] = useState("merchandiser");
  const [tmCountry, setTmCountry] = useState("India");
  const [tmCreating, setTmCreating] = useState(false);

  // ── Edit modal ────────────────────────────────────────────────────────────
  const [editingUser, setEditingUser] = useState(null);
  const [editName, setEditName] = useState("");
  const [editCategory, setEditCategory] = useState("merchandiser");
  const [editCountry, setEditCountry] = useState("India");
  const [editSaving, setEditSaving] = useState(false);
  const [editMessage, setEditMessage] = useState("");
  const [editMessageType, setEditMessageType] = useState("success");
  const [editCurrentPassword, setEditCurrentPassword] = useState("");
  const [editNewPassword, setEditNewPassword] = useState("");
  const [pwChanging, setPwChanging] = useState(false);

  // ── Delete state ──────────────────────────────────────────────────────────
  const [confirmDeleteUser, setConfirmDeleteUser] = useState(null);
  const [deletingUid, setDeletingUid] = useState(null);

  // ── Table state ───────────────────────────────────────────────────────────
  const [sortDir, setSortDir] = useState("asc");
  const [filterCategory, setFilterCategory] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);

  // ── Derived data ──────────────────────────────────────────────────────────
  const teamUsers = useMemo(
    () => users.filter((u) => TEAM_CATEGORIES.includes(u.category)),
    [users],
  );

  const counts = useMemo(
    () => ({
      merchandisers: teamUsers.filter((u) => u.category === "merchandiser")
        .length,
      qc: teamUsers.filter((u) => u.category === "quality-control").length,
      designers: teamUsers.filter((u) => u.category === "designer").length,
      logistics: teamUsers.filter((u) => u.category === "logistics").length,
      branding: teamUsers.filter((u) => u.category === "branding-retail")
        .length,
      total: teamUsers.length,
    }),
    [teamUsers],
  );

  const filteredTeam = useMemo(
    () =>
      teamUsers
        .filter(
          (u) => filterCategory === "all" || u.category === filterCategory,
        )
        .sort((a, b) => {
          const na = (a.name || "").toLowerCase();
          const nb = (b.name || "").toLowerCase();
          return sortDir === "asc"
            ? na.localeCompare(nb)
            : nb.localeCompare(na);
        }),
    [teamUsers, filterCategory, sortDir],
  );
  const totalPages = Math.ceil(filteredTeam.length / ITEMS_PER_PAGE);
  const paginatedTeam = useMemo(
    () =>
      filteredTeam.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE,
      ),
    [filteredTeam, currentPage],
  );

  // ── Handlers ──────────────────────────────────────────────────────────────
  const handleCreateTeamMember = useCallback(async () => {
    if (!tmUsername.trim() || !tmPassword) {
      showToast("Please fill in both fields.", "error");
      return;
    }
    setTmCreating(true);
    let secondaryApp = null;
    try {
      const email = `${tmUsername.trim()}@synaptex.app`;
      secondaryApp = initializeApp(firebaseConfig, "secondary");
      const secondaryAuth = getAuth(secondaryApp);
      const { user: fbUser } = await createUserWithEmailAndPassword(
        secondaryAuth,
        email,
        tmPassword,
      );
      await setDoc(doc(db, "users", fbUser.uid), {
        name: tmUsername.trim(),
        isAdmin: false,
        category: tmCategory,
        country: tmCountry,
        isBuyer: false,
        linkedSupplierUid: null,
        sharedOrder: false,
        sharedWith: null,
      });
      await secondaryAuth.signOut();
      showToast(`Team member "${tmUsername.trim()}" created successfully.`);
      setTmUsername("");
      setTmPassword("");
      setTmCategory("merchandiser");
      setTmCountry("India");
    } catch (err) {
      const msgs = {
        "auth/email-already-in-use": "That username is already taken.",
        "auth/weak-password": "Password must be at least 6 characters.",
        "auth/invalid-email": "Invalid username format.",
      };
      showToast(msgs[err.code] || err.message, "error");
    } finally {
      if (secondaryApp) await deleteApp(secondaryApp).catch(() => {});
      setTmCreating(false);
    }
  }, [tmUsername, tmPassword, tmCategory, tmCountry, showToast]);

  const handleOpenEdit = useCallback((user) => {
    setEditingUser(user);
    setEditName(user.name || "");
    setEditCategory(user.category || "merchandiser");
    setEditCountry(user.country || "India");
    setEditMessage("");
    setEditCurrentPassword("");
    setEditNewPassword("");
  }, []);

  const handleSaveEdit = useCallback(async () => {
    if (!editingUser) return;
    setEditSaving(true);
    try {
      const updatedFields = {
        name: editName.trim() || editingUser.name,
        category: editCategory,
        country: editCountry,
      };
      await updateDoc(doc(db, "users", editingUser.uid), updatedFields);
      setUsers((prev) =>
        prev.map((u) =>
          u.uid === editingUser.uid ? { ...u, ...updatedFields } : u,
        ),
      );
      setEditMessage("Saved.");
      setEditMessageType("success");
      setTimeout(() => {
        setEditingUser(null);
        setEditMessage("");
      }, 1200);
    } catch (err) {
      setEditMessage(err.message);
      setEditMessageType("error");
    } finally {
      setEditSaving(false);
    }
  }, [editingUser, editName, editCategory, editCountry, setUsers]);

  const handleChangePassword = useCallback(async () => {
    if (!editCurrentPassword.trim() || !editNewPassword.trim()) {
      setEditMessage("Please fill in both password fields.");
      setEditMessageType("error");
      return;
    }
    setPwChanging(true);
    let secondaryApp = null;
    try {
      const email = `${editingUser.name}@synaptex.app`;
      secondaryApp = initializeApp(firebaseConfig, "secondary");
      const secondaryAuth = getAuth(secondaryApp);
      const credential = await signInWithEmailAndPassword(
        secondaryAuth,
        email,
        editCurrentPassword,
      );
      await updatePassword(credential.user, editNewPassword);
      await secondaryAuth.signOut();
      setEditCurrentPassword("");
      setEditNewPassword("");
      setEditMessage("Password updated.");
      setEditMessageType("success");
    } catch (err) {
      const msgs = {
        "auth/wrong-password": "Current password is incorrect.",
        "auth/weak-password": "New password must be at least 6 characters.",
        "auth/too-many-requests": "Too many attempts — try again later.",
      };
      setEditMessage(msgs[err.code] || err.message);
      setEditMessageType("error");
    } finally {
      if (secondaryApp) await deleteApp(secondaryApp).catch(() => {});
      setPwChanging(false);
    }
  }, [editingUser, editCurrentPassword, editNewPassword]);

  const closeEdit = useCallback(() => {
    setEditingUser(null);
    setEditMessage("");
    setEditCurrentPassword("");
    setEditNewPassword("");
  }, []);

  const handleDeleteUser = useCallback(
    async (user) => {
      setDeletingUid(user.uid);
      try {
        const auth = getAuth();
        const idToken = await auth.currentUser.getIdToken();

        const res = await fetch("/.netlify/functions/delete-user", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ uid: user.uid }),
        });

        const data = await res.json();
        if (!res.ok) throw new Error(data.error || "Delete failed");

        setUsers((prev) => prev.filter((u) => u.uid !== user.uid));
        showToast(`"${user.name}" deleted successfully.`);
      } catch (err) {
        showToast(err.message, "error");
      } finally {
        setDeletingUid(null);
        setConfirmDeleteUser(null);
      }
    },
    [showToast, setUsers],
  );

  return (
    <>
      {/* ── Overview ── */}
      <h2 className={styles.sectionTitle}>Team Overview</h2>
      <div className={styles.statsRow}>
        <StatCard form label="New Team Member">
          <input
            className={styles.input}
            placeholder="Username"
            value={tmUsername}
            onChange={(e) => setTmUsername(e.target.value)}
          />
          <input
            className={styles.input}
            placeholder="Password"
            type="password"
            value={tmPassword}
            onChange={(e) => setTmPassword(e.target.value)}
          />
          <SelectWrapper
            value={tmCategory}
            onChange={(e) => setTmCategory(e.target.value)}
          >
            <option value="merchandiser">Merchandiser</option>
            <option value="quality-control">Quality Control</option>
            <option value="designer">Designer</option>
            <option value="logistics">Logistics</option>
            <option value="branding-retail">Branding &amp; Retail</option>
          </SelectWrapper>
          <SelectWrapper
            value={tmCountry}
            onChange={(e) => setTmCountry(e.target.value)}
          >
            {COUNTRIES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </SelectWrapper>
          <button
            className={`${styles.btn} ${styles.btnPrimary}`}
            onClick={handleCreateTeamMember}
            disabled={tmCreating}
          >
            {tmCreating ? "Creating…" : "Add Member"}
          </button>
        </StatCard>

        <StatCard
          label="Merchandisers & QC"
          value={`${counts.merchandisers} | ${counts.qc}`}
          sub={`${counts.merchandisers} Merchandisers | ${counts.qc} QC`}
        />
        <StatCard
          label="Designers"
          value={counts.designers}
          sub="Creative & technical design"
        />
        <StatCard
          label="Logistics"
          value={counts.logistics}
          sub="Shipping & supply chain"
        />
        <StatCard
          label="Branding & Retail"
          value={counts.branding}
          sub="Brand & retail operations"
        />
        <StatCard
          label="Total Members"
          value={counts.total}
          sub="All registered team members"
        />
      </div>

      <div className={styles.rule} />

      {/* ── Table ── */}
      <h2 className={styles.sectionTitle}>Team</h2>
      {teamUsers.length === 0 ? (
        <p className={styles.stateText}>No team members found.</p>
      ) : (
        <>
          <div className={styles.tableWrapper}>
            <div className={styles.tableControls}>
              <div className={styles.tableFilterGroup}>
                <span className={styles.tableFilterLabel}>Filter by Role</span>
                <SelectWrapper
                  value={filterCategory}
                  selectClassName={styles.tableFilterSelect}
                  onChange={(e) => {
                    setFilterCategory(e.target.value);
                    setCurrentPage(1);
                  }}
                >
                  <option value="all">All</option>
                  <option value="merchandiser">Merchandiser</option>
                  <option value="quality-control">Quality Control</option>
                  <option value="designer">Designer</option>
                  <option value="logistics">Logistics</option>
                  <option value="branding-retail">Branding &amp; Retail</option>
                </SelectWrapper>
              </div>
            </div>

            <div className={styles.tableScroll}>
              <table className={styles.table}>
                <thead>
                  <tr className={styles.tableHeadRow}>
                    <th className={styles.th}>#</th>
                    <th className={styles.th}>
                      <button
                        className={styles.sortBtn}
                        onClick={() => {
                          setSortDir((d) => (d === "asc" ? "desc" : "asc"));
                          setCurrentPage(1);
                        }}
                      >
                        Name{" "}
                        <span className={styles.sortIcon}>
                          {sortDir === "asc" ? "↑" : "↓"}
                        </span>
                      </button>
                    </th>
                    <th className={styles.th}>Role</th>
                    <th className={styles.th}>Country</th>
                    <th className={styles.th}></th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedTeam.map((user, index) => (
                    <tr key={user.uid} className={styles.tableRow}>
                      <td className={styles.td}>
                        {(currentPage - 1) * ITEMS_PER_PAGE + index + 1}
                      </td>
                      <td className={styles.td}>
                        {user.category === "merchandiser" ? (
                          <button
                            className={styles.supplierLink}
                            onClick={() => navigate(`/leads/${user.uid}`)}
                          >
                            {user.name}
                          </button>
                        ) : user.category === "quality-control" ? (
                          <button
                            className={styles.supplierLink}
                            onClick={() => navigate(`/quality/${user.uid}`)}
                          >
                            {user.name}
                          </button>
                        ) : (
                          user.name
                        )}
                      </td>
                      <td className={styles.td}>
                        <CategoryBadge category={user.category} />
                      </td>
                      <td className={styles.td}>{user.country || "—"}</td>
                      <td className={styles.td}>
                        <button
                          className={styles.editBtn}
                          onClick={() => handleOpenEdit(user)}
                          title="Edit team member"
                        >
                          ✎
                        </button>
                        <button
                          className={`${styles.editBtn} ${styles.deleteBtn}`}
                          onClick={() => setConfirmDeleteUser(user)}
                          title="Delete team member"
                          disabled={deletingUid === user.uid}
                        >
                          {deletingUid === user.uid ? "…" : "🗑"}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {/* ── Delete Confirm Modal ── */}
      {confirmDeleteUser && (
        <Modal
          title="Delete Team Member"
          onClose={() => setConfirmDeleteUser(null)}
        >
          <p style={{ marginBottom: 16 }}>
            Permanently delete <strong>{confirmDeleteUser.name}</strong>? This
            will remove their Firestore data and Firebase Auth account. This
            cannot be undone.
          </p>
          <div style={{ display: "flex", gap: 8 }}>
            <button
              className={`${styles.btn} ${styles.btnDanger}`}
              onClick={() => handleDeleteUser(confirmDeleteUser)}
              disabled={deletingUid === confirmDeleteUser.uid}
            >
              {deletingUid === confirmDeleteUser.uid
                ? "Deleting…"
                : "Yes, Delete"}
            </button>
            <button
              className={styles.btn}
              onClick={() => setConfirmDeleteUser(null)}
            >
              Cancel
            </button>
          </div>
        </Modal>
      )}

      {/* ── Edit Modal ── */}
      {editingUser && (
        <Modal title={`Edit — ${editingUser.name}`} onClose={closeEdit}>
          <div className={styles.formInner}>
            <input
              className={styles.input}
              placeholder="Name"
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
            />
            <SelectWrapper
              value={editCategory}
              onChange={(e) => setEditCategory(e.target.value)}
            >
              <optgroup label="Supplier">
                <option value="woven">Woven</option>
                <option value="knitted">Knitted</option>
                <option value="non-woven">Non-Woven</option>
                <option value="common">Common</option>
              </optgroup>
              <option value="merchandiser">Merchandiser</option>
              <option value="quality-control">Quality Control</option>
              <option value="designer">Designer</option>
              <option value="logistics">Logistics</option>
              <option value="branding-retail">Branding &amp; Retail</option>
            </SelectWrapper>
            <SelectWrapper
              value={editCountry}
              onChange={(e) => setEditCountry(e.target.value)}
            >
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </SelectWrapper>

            <button
              className={`${styles.btn} ${styles.btnPrimary}`}
              onClick={handleSaveEdit}
              disabled={editSaving}
            >
              {editSaving ? "Saving…" : "Save Changes"}
            </button>

            <div
              style={{
                marginTop: 16,
                paddingTop: 16,
                borderTop: "1px solid var(--border)",
                width: "100%",
              }}
            >
              <span
                className={styles.toggleLabelText}
                style={{ display: "block", marginBottom: 10 }}
              >
                Change Password
              </span>
              <div className={styles.formInner}>
                <input
                  className={styles.input}
                  type="password"
                  placeholder="Current password"
                  value={editCurrentPassword}
                  onChange={(e) => setEditCurrentPassword(e.target.value)}
                />
                <input
                  className={styles.input}
                  type="password"
                  placeholder="New password"
                  value={editNewPassword}
                  onChange={(e) => setEditNewPassword(e.target.value)}
                />
                <button
                  className={`${styles.btn} ${styles.btnPrimary}`}
                  onClick={handleChangePassword}
                  disabled={pwChanging}
                >
                  {pwChanging ? "Updating…" : "Update Password"}
                </button>
              </div>
            </div>

            {editMessage && (
              <p
                className={`${styles.modalMessage} ${editMessageType === "success" ? styles.modalMessageSuccess : styles.modalMessageError}`}
              >
                {editMessage}
              </p>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
